"use client";

import React, { useState } from "react";
import { ResponsiveModal, ModalContainer, MODAL_IDS } from "@wtc/ui";
import { Button } from "@/components/ui/button";
import { Textarea } from "@wtc/ui";
import { useUpdateDeal } from "@/features/deals/hooks/use-deals-hooks";
import type { MarkDealWonModalData } from "@/lib/modals/data/modal-data-types";

// This modal is mounted globally (app/layout.tsx → DetailPageModals), OUTSIDE
// any detail-action provider. It therefore takes the deal id ONLY from the
// global-modal-stack data payload (supplied by the action handler /
// DealDetailActionsBar) — never from React context. The write happens via
// useUpdateDeal(), whose mutation already invalidates the deals cache.
// See .claude/rules/GLOBAL_MODAL_NO_ACTION_CONTEXT.md and ACTION_MODAL_MOUNTS.md.

export function MarkDealWonModal() {
  return (
    <ModalContainer<MarkDealWonModalData> modalId={MODAL_IDS.deals.markWon}>
      {({ isOpen, data, onClose }) => (
        <MarkDealWonModalContent
          isOpen={isOpen}
          data={data}
          onClose={onClose}
        />
      )}
    </ModalContainer>
  );
}

interface MarkDealWonModalContentProps {
  isOpen: boolean;
  data?: MarkDealWonModalData;
  onClose: () => void;
}

function MarkDealWonModalContent({
  isOpen,
  data,
  onClose,
}: MarkDealWonModalContentProps) {
  const updateDealMutation = useUpdateDeal();
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const dealId = data?.dealId;

  // Global mount: with no deal id in the modal data payload there is nothing to
  // act on. Never fall back to context (always null here) — render nothing.
  if (!dealId) return null;

  const handleMarkWon = async () => {
    setIsLoading(true);
    try {
      // Append the win note to existing notes (passed via payload) — never clobber.
      const base = (data?.currentNotes ?? "").trim();
      const appendedNotes = notes
        ? base
          ? `${base}\n\n[Won] ${notes}`
          : `[Won] ${notes}`
        : null;
      await updateDealMutation.mutateAsync({
        id: dealId,
        stage: "deal_won",
        won_at: new Date().toISOString(),
        ...(appendedNotes ? { notes: appendedNotes } : {}),
      });

      onClose();
    } catch (error) {
      console.error("Error marking deal as won:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ResponsiveModal
      globalModalId={MODAL_IDS.deals.markWon}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Mark Deal as Won"
      description="Mark this deal as won"
      maxWidth="sm"
    >
      <div className="p-4">
        <div className="space-y-4">
          <div className="bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-800 rounded-lg p-3">
            <p className="text-sm text-green-800 dark:text-green-200">
              This deal will be marked as won and the won_at timestamp will be recorded.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Notes (Optional)</label>
            <Textarea
              placeholder="Add any notes about winning this deal..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleMarkWon} disabled={isLoading} className="bg-green-600 hover:bg-green-700">
              {isLoading ? "Marking as Won..." : "Mark as Won"}
            </Button>
          </div>
        </div>
      </div>
    </ResponsiveModal>
  );
}
